// src/hooks/useBenchmarkApi.tsx

import {
  createContext,
  Dispatch,
  ReactNode,
  SetStateAction,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'

import { SensorId } from '@/types/domain'

import { authHeader, notifyError, notifySuccess } from '../api/'
import { usePreloader } from '../components/'
import { languageConstants as LC } from '../constants/'

import { refreshToken } from './auth/'

const { REACT_APP_API_URL } = import.meta.env

type BenchmarkContextValue = {
  byId: Record<string, unknown[]>
  setById: Dispatch<SetStateAction<Record<string, unknown[]>>>
  refresh: (args?: { sensorId?: SensorId; force?: boolean }) => Promise<unknown>
  getBySensorId: (
    sensorId: SensorId,
    args?: { force?: boolean }
  ) => Promise<unknown>
}

const BenchmarkCtx = createContext<BenchmarkContextValue>({
  byId: {},
  setById: () => {},
  refresh: async () => null,
  getBySensorId: async () => null,
})

type Props = {
  children: ReactNode
}

export const BenchmarkDataProvider = ({ children }: Props) => {
  const { start, stop } = usePreloader()
  const [byId, setById] = useState<Record<string, unknown[]>>({})
  const reqsRef = useRef<Record<string, Promise<unknown> | null>>({})

  const withRequest = useCallback(async fn => {
    try {
      return await fn()
    } catch (error) {
      const status = error?.status ?? error?.response?.status
      if (status === 401 || status === 403) {
        try {
          await refreshToken()
          return await fn()
        } catch (e2) {
          notifyError(`Benchmark DataProvider: ${e2?.message}`)
          return null
        }
      }
      notifyError(`Benchmark DataProvider: ${error?.message}`)
      return null
    }
  }, [])

  const withPreloader = useCallback(
    async fn => {
      start()
      try {
        return await fn()
      } finally {
        stop()
      }
    },
    [start, stop]
  )

  const fetchBySensorId = useCallback(
    async (id: SensorId, { force = false }: { force?: boolean } = {}) => {
      if (!id) return []
      const key = String(id)
      if (reqsRef.current[key] && !force) return reqsRef.current[key]

      reqsRef.current[key] = (async () => {
        try {
          const data = await withPreloader(() =>
            withRequest(async () => {
              const url = `${REACT_APP_API_URL}/benchmark/v1/all/${id}`

              await refreshToken()

              const options = { method: 'GET', headers: { ...authHeader() } }
              const response = await fetch(url, options)
              if (response.status === 204) return []
              if (!response.ok) {
                const text = await response.text()
                throw {
                  message: text || `HTTP ${response.status}`,
                  status: response.status,
                }
              }
              const payload = await response.json()
              return payload?.data ?? []
            })
          )

          setById(prev => ({ ...prev, [key]: Array.isArray(data) ? data : [] }))
          return Array.isArray(data) ? data : []
        } finally {
          reqsRef.current[key] = null
        }
      })()

      return reqsRef.current[key]
    },
    [withRequest, withPreloader]
  )

  const ctxValue = useMemo(
    () => ({
      byId,
      setById,
      refresh: ({
        sensorId,
        force = false,
      }: { sensorId?: SensorId; force?: boolean } = {}) =>
        fetchBySensorId(Number(sensorId), { force }),
      getBySensorId: (id: SensorId, { force = false } = {}) =>
        fetchBySensorId(id, { force }),
    }),
    [byId, fetchBySensorId]
  )

  return (
    <BenchmarkCtx.Provider value={ctxValue}>{children}</BenchmarkCtx.Provider>
  )
}

export const useBenchmark = (id: SensorId) => {
  const { byId, getBySensorId } = useContext(BenchmarkCtx)
  const key = String(id || '')
  const cached = key ? byId[key] || null : null

  const [data, setData] = useState([])
  const [isError, setError] = useState(null)
  const [isLoading, setLoading] = useState(false)

  useEffect(() => {
    let cancelled = false

    const load = async () => {
      if (!id) return

      setLoading(true)
      setError(null)

      try {
        const result = cached ?? (await getBySensorId(id))
        if (!cancelled) setData(Array.isArray(result) ? result : [])
      } catch (error: any) {
        if (!cancelled) setError(error?.message || 'Fehler beim Laden')
      } finally {
        if (!cancelled) setLoading(false)
      }
    }

    load()

    return () => {
      cancelled = true
    }
  }, [id, cached, getBySensorId])

  return { data, isError, isLoading }
}

export const useBenchmarkApi = () => {
  const { refresh } = useContext(BenchmarkCtx)

  const withRequest = useCallback(async fn => {
    try {
      return await fn()
    } catch (error) {
      const status = error?.status ?? error?.response?.status
      if (status === 401 || status === 403) {
        try {
          await refreshToken()
          return await fn()
        } catch (e2) {
          notifyError(`BenchmarkAPI: ${e2?.message}`)
          return null
        }
      }
      notifyError(`BenchmarkAPI: ${error?.message}`)
      return null
    }
  }, [])

  const benchmarkData = useCallback(data => {
    const {
      entity_id,
      sensor_id,
      benchmark_name,
      value,
      unit,
      period_start,
      period_end,
    } = data
    return {
      data: {
        id: entity_id,
        type: 'benchmark',
        attributes: {
          sensor_id: parseFloat(sensor_id),
          period_start,
          period_end,
          benchmark_name,
          value: parseFloat(value),
          unit,
        },
      },
    }
  }, [])

  const save = useCallback(
    async data =>
      withRequest(async () => {
        const { benchmark_name } = data || {}
        if (!benchmark_name) {
          notifyError(LC.BENCHMARK_NAME_REQUIRED)
          return null
        }

        await refreshToken()

        const url = `${REACT_APP_API_URL}/benchmark/v1/`
        const options = {
          headers: { ...authHeader(), 'Content-Type': 'application/json' },
          method: 'POST',
          body: JSON.stringify(benchmarkData(data)),
        }

        const response = await fetch(url, options)
        if (!response.ok) {
          const text = await response.text()
          throw {
            message: text || `HTTP ${response.status}`,
            status: response.status,
          }
        }

        let newId = null
        try {
          const payload = await response.json()
          newId = payload?.id ?? payload?.data?.id ?? null
        } catch {
          // kein body/204 – ok
        }

        notifySuccess(LC.BENCHMARK_SAVED)
        await refresh({ sensorId: data?.sensor_id, force: true })
        return newId
      }),
    [benchmarkData, refresh, withRequest]
  )

  const update = useCallback(
    async (id, data) =>
      withRequest(async () => {
        if (!id) {
          notifyError(LC.BENCHMARK_ID_REQUIRED)
          return null
        }

        await refreshToken()

        const url = `${REACT_APP_API_URL}/benchmark/v1/${id}`
        const options = {
          headers: { ...authHeader(), 'Content-Type': 'application/json' },
          method: 'PATCH',
          body: JSON.stringify(benchmarkData(data)),
        }
        const response = await fetch(url, options)
        if (!response.ok) {
          const text = await response.text()
          throw {
            message: text || `HTTP ${response.status}`,
            status: response.status,
          }
        }
        notifySuccess(LC.BENCHMARK_UPDATED)

        await refresh({ sensorId: data?.sensor_id, force: true })
        return id
      }),
    [benchmarkData, refresh, withRequest]
  )

  const remove = useCallback(
    async (id, { sensorId }: { sensorId?: SensorId } = {}) =>
      withRequest(async () => {
        if (!id) {
          notifyError(LC.BENCHMARK_ID_REQUIRED)
          return false
        }

        await refreshToken()

        const url = `${REACT_APP_API_URL}/benchmark/v1/${id}`
        const options = {
          headers: { ...authHeader(), 'Content-Type': 'application/json' },
          method: 'DELETE',
        }
        const response = await fetch(url, options)
        if (!response.ok) {
          const text = await response.text()
          throw {
            message: text || `HTTP ${response.status}`,
            status: response.status,
          }
        }

        notifySuccess(LC.BENCHMARK_DELETED)

        if (sensorId) await refresh({ sensorId: Number(sensorId), force: true })

        return true
      }),
    [refresh, withRequest]
  )

  return { save, update, remove, refresh }
}
